import { GridColDef, GridRenderCellParams, GridRowClassNameParams } from '@mui/x-data-grid-pro';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { StyledTableWrapper } from './PartAccordianFragment.styles';
import { PartAccordianFragmentProps, StructurePartListType } from './PartsAccordianFragment.props';
import { ColorProps, DatagridSortingTypes } from '../../../../@types';
import { DataGrid } from '../../../../components/atoms/DataGrid';
import { FormControlLabel } from '../../../../components/atoms/FormControlLabel';
import { Panel } from '../../../../components/atoms/Panel';
import { PermissionLink } from '../../../../components/atoms/PermissionLink';
import { Switch } from '../../../../components/atoms/Switch';
import { TablePill } from '../../../../components/atoms/TablePill';
import { GridDensityTypes, GridFeatureModeConstant } from '../../../../configs/enums';
import { useGetAllPartsForStructures } from '../../../../queries/structure-query';
import { formatDate } from '../../../../utils/common';

export const PartAccordianFragment: React.FC<PartAccordianFragmentProps> = ({ nodeId }) => {
  const [enableChildLocationParts, setEnableChildLocationParts] = useState<boolean>(false);
  const [partsData, setPartsData] = useState<StructurePartListType>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const getAllPartsForStructures = useGetAllPartsForStructures(
    {
      currentPage: page,
      limit: pageSize,
      total: 0,
    },
    nodeId,
    enableChildLocationParts
  );

  useEffect(() => {
    getAllPartsForStructures.data && setPartsData(getAllPartsForStructures.data);
  }, [getAllPartsForStructures.data]);

  const columns: GridColDef[] = [
    {
      field: 'serial1',
      headerName: 'Serial #1',
      minWidth: 120,
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.row.id ? (
          <PermissionLink permission="part.search" href={`/parts/${params.row.id}`}>
            {params.row.serial1}
          </PermissionLink>
        ) : (
          params.row.serial1
        ),
    },
    {
      field: 'partTypeName',
      headerName: 'Part Type Name',
      minWidth: 220,
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.row.partTypeId ? (
          <PermissionLink
            permission="configure.part_types.search"
            href={`/configure/partTypes/${params.row.partTypeId}`}
          >
            {params.row.partTypeName}
          </PermissionLink>
        ) : (
          params.row.partTypeName
        ),
    },
    {
      field: 'partTypeCategory',
      headerName: 'Part Type Category',
      minWidth: 200,
    },
    {
      field: 'partConditionName',
      headerName: 'Condition',
      minWidth: 200,
    },
    {
      field: 'softwareName',
      headerName: 'Software',
      minWidth: 150,
    },
    {
      field: 'firmwareName',
      headerName: 'Firmware',
      minWidth: 150,
    },
    {
      field: 'hardwareName',
      headerName: 'Hardware',
      minWidth: 150,
    },
    {
      field: 'fleetTagName',
      headerName: 'Fleet Tag',
      minWidth: 150,
    },
    {
      field: 'qty',
      headerName: 'Qty',
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => params.row.qty > 0 && params.row.qty,
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Last Updated At',
      minWidth: 250,
      valueGetter: (_value, row) => formatDate(row.updatedAt),
    },
  ];

  const handleToggleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    // Back to the first page: the page index is part of the query key, so
    // widening the filter while on page 3 can land on an empty grid even though
    // matching records exist on page 1.
    setPage(0);
    setEnableChildLocationParts(event.target.checked);
  };

  return (
    <Panel
      title="Parts"
      titleAdornment={
        partsData?.total !== undefined ? (
          <TablePill tone="neutral">{partsData.total}</TablePill>
        ) : undefined
      }
      bodyPadding={0}
      actions={
        <FormControlLabel
          control={
            <Switch
              color={ColorProps.Primary}
              checked={enableChildLocationParts}
              onChange={handleToggleChange}
            />
          }
          label="Include child records"
        />
      }
    >
      <StyledTableWrapper>
        <DataGrid
          v4Skin
          density={GridDensityTypes.Standard}
          columns={columns}
          rows={partsData?.data || []}
          getRowId={(row) => row?.index}
          rowCount={partsData?.total}
          loading={getAllPartsForStructures.isLoading}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableDensitySelector={true}
          rowsPerPageOptions={[15]}
          pagination={true}
          page={page}
          pageSize={pageSize}
          paginationMode={GridFeatureModeConstant.server}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'lastUpdatedAt', sort: DatagridSortingTypes.Descending }],
            },
          }}
          getRowClassName={(params: GridRowClassNameParams) =>
            `super-app-theme--${params.row.index}`
          }
          autoHeight={true}
        />
      </StyledTableWrapper>
    </Panel>
  );
};
